import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '../Typography';
import styles from './styles.module.scss';

interface IOtpInputProps {
  length?: number;
  onChange: (otp: string) => void;
  label?: string;
  size?: 'lg' | 'md' | 'sm' | 'auto';
  disabled?: boolean;
  readOnly?: boolean;
  classname?: string;
  errorMessage?: string;
  required?: boolean;
  inputClassName?: string;
  isLightInp?: boolean;
  isTransparent?: boolean;
  value?: string;
}

const OtpInput: React.FC<IOtpInputProps> = ({
  length = 6,
  onChange,
  label,
  size = 'md',
  disabled,
  readOnly,
  classname,
  errorMessage,
  required,
  inputClassName,
  isLightInp,
  isTransparent,
  value = '',
}) => {
  const { t } = useTranslation();

  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (value: string, index: number): void => {
    if (!/^\d*$/.test(value)) return;
    const updatedOtp = [...otp];
    updatedOtp[index] = value.slice(-1);
    setOtp(updatedOtp);
    onChange(updatedOtp.join(''));

    if (value && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number): void => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const updatedOtp = [...otp];
      updatedOtp[index - 1] = '';
      setOtp(updatedOtp);
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    const pasteData = e.clipboardData.getData('Text').slice(0, length);
    if (!/^\d+$/.test(pasteData)) return;

    const updatedOtp = pasteData.split('').slice(0, length);
    setOtp(updatedOtp);
    onChange(updatedOtp.join(''));

    const lastIndex = Math.min(updatedOtp.length, length) - 1;
    inputsRef.current[lastIndex]?.focus();
  };

  // Effects
  useEffect(() => {
    if (value && /^\d+$/.test(value)) {
      const updatedOtp = value.slice(0, length).split('');
      setOtp(
        Array(length)
          .fill('')
          .map((_, i) => updatedOtp[i] || '')
      );
    }
  }, [value, length]);

  return (
    <div className={cn(styles.input, { [styles.input_error]: !!errorMessage }, classname)}>
      {label && label !== 'phoneNumber_label2' && (
        <Typography className={styles.input_label} color="gray-300" variant="body5" fontWeight="400">
          {t(label)} {required && <span>*</span>}
        </Typography>
      )}
      <div className={styles.otp_input_container}>
        {otp.map((digit, i) => (
          <input
            key={i}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            ref={(el) => (inputsRef.current[i] = el)}
            onChange={(e) => handleChange(e.target.value, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onPaste={handlePaste}
            disabled={disabled}
            readOnly={readOnly}
            className={cn(styles.otp_input, inputClassName, {
              [styles[`otp_input_size__${size}`]]: size,
              [styles.light]: isLightInp,
              [styles.transparent]: isTransparent,
            })}
          />
        ))}
      </div>
      {!!errorMessage && (
        <Typography color="error" variant="body5">
          {errorMessage}
        </Typography>
      )}
    </div>
  );
};

export default OtpInput;
